/**
 * API Error Utilities
 *
 * Clean helpers for handling API errors without importing AxiosError everywhere.
 * Follows the principle: catch errors at boundaries, handle them cleanly.
 */

import { AxiosError } from "axios"

/**
 * Check if error is an API error with response
 */
export function isApiError(error: unknown): error is AxiosError {
  return error instanceof AxiosError && !!error.response
}

/**
 * Get HTTP status code from error
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isApiError(error) && error.response) {
    return error.response.status
  }
  return undefined
}

/**
 * The best sentence available for this error: the SERVER's, then the error's own,
 * then the caller's default.
 *
 * 🚨 THE ORDER IS THE WHOLE CONTRACT, AND INVERTING IT IS SILENT.
 * {@link serverErrorMessage} runs FIRST, because `AxiosError.message` is always a
 * string and always the boilerplate `"Request failed with status code 409"`. So a
 * reader that consults the error before the body finds a plausible sentence every
 * time and never reaches the one a person can act on — the same shape as reading
 * axios's own `code` instead of the server's.
 *
 * It read only the TOP-LEVEL `message` until 2026-09-07, which meant every error
 * rendered through the canonical envelope — every `DomainHttpError`, every payload
 * naming a SCREAMING_SNAKE code, every `/api/public/v1` route — answered with that
 * boilerplate across all 47 call sites, most of which put the result straight into
 * a toast or an inline error.
 *
 * The `as Record<string, unknown>` cast that used to sit here is gone with it:
 * {@link responseBodyOf} proves the shape it reads instead of asserting it.
 */
export function getErrorMessage(error: unknown, defaultMessage = "An error occurred"): string {
  const fromServer = serverErrorMessage(error)
  if (fromServer !== null) return fromServer

  if (isApiError(error)) {
    return error.message || defaultMessage
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === "string") {
    return error
  }

  return defaultMessage
}

/**
 * Check if error is a specific HTTP status code
 */
export function isErrorStatus(error: unknown, status: number): boolean {
  return getErrorStatus(error) === status
}

/**
 * Check if error is a conflict (409)
 */
export function isConflictError(error: unknown): boolean {
  return isErrorStatus(error, 409)
}

/**
 * Check if error is unauthorized (401)
 */
export function isUnauthorizedError(error: unknown): boolean {
  return isErrorStatus(error, 401)
}

/**
 * Check if error is forbidden (403)
 */
export function isForbiddenError(error: unknown): boolean {
  return isErrorStatus(error, 403)
}

/**
 * Check if error is not found (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return isErrorStatus(error, 404)
}

/**
 * Check if error is server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  const status = getErrorStatus(error)
  return status !== undefined && status >= 500
}

/**
 * Get error data payload from response
 */
export function getErrorData(error: unknown): Record<string, unknown> | undefined {
  if (isApiError(error) && error.response) {
    return error.response.data as Record<string, unknown>
  }
  return undefined
}

/**
 * The machine-readable code the SERVER sent, or `null`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 THE DEPTH IS A WIRE INVARIANT, AND THIS IS THE ONLY SANCTIONED READER
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every error this backend renders with a structured code puts that code at
 * `data.error.code` — the `createApiError` envelope,
 * `{ success: false, error: { code, message } }` — on every route, whatever
 * filter produced it. A body carrying no structured code (a bare
 * `new NotFoundException("msg")`) has no code to read and answers `null`.
 *
 * **Read a code through this function and nowhere else.** Two other spellings
 * are bugs, and neither is a near miss:
 *
 * · **`data.code`** — the FLAT shape. It was what ~180 hand-built
 *   `HttpException` payloads put on the wire until the depth was normalised, so
 *   a reader written to it still typechecks, resolves `undefined` for every
 *   code, and drops through to whatever generic arm sits under the match.
 *   Nothing throws, nothing logs, and the screen simply says the wrong thing.
 *
 * · **`error.code`** — the AXIOS one, and the worse of the two. `AxiosError`
 *   carries its own `code`, which is `"ERR_BAD_REQUEST"` on every 4xx — so a
 *   top-level read always finds a string, and that string always fails every
 *   comparison. `Vibe/utils/vibe-authorize-failure.ts` shipped that way once:
 *   every branch of its mapper was unreachable in production and the page told
 *   people "trying again is safe" about refusals for which that could never
 *   come true.
 *
 * Deliberately a structural walk rather than a call to {@link getErrorData}:
 * that helper gates on `instanceof AxiosError`, and this is handed a
 * react-query `onError` argument whose provenance nothing guarantees. Each step
 * proves the shape it is about to read instead of asserting it, because a body
 * is genuinely `unknown` here — an error response is not even certainly JSON.
 *
 * A body handed over ALREADY UNWRAPPED is accepted too, so a caller holding the
 * envelope rather than the thrown error keeps working. What is deliberately NOT
 * accepted is a bare top-level `code` on an object that also carries a
 * `response` — that is the axios trap above, wearing the envelope's clothes.
 */
export function serverErrorCode(error: unknown): string | null {
  const body = responseBodyOf(error)
  if (body === null) return null

  const envelope: unknown = body.error
  if (!isRecordLike(envelope)) return null

  const code: unknown = envelope.code
  return typeof code === "string" ? code : null
}

/**
 * The message the SERVER wrote for the user, or `null`.
 *
 * Same wire invariant as {@link serverErrorCode} and the same two shapes, in the
 * order the backend renders them:
 *
 * · **`data.error.message`** — the canonical envelope. Every `DomainHttpError`
 *   goes through `createApiError(code, message)`, and `HttpExceptionFilter`
 *   renders any payload naming a SCREAMING_SNAKE code the same way. This is the
 *   depth ~180 production error sites moved to.
 * · **`data.message`** — the top-level i18n contract (`{ statusCode, type, code,
 *   message }`, lower_snake) and NestJS's own default body. `STRUCTURED_ERROR_CODE`
 *   rejects a lower_snake code on purpose, so that family is never enveloped and
 *   still carries its message here.
 *
 * `null` is a DISCRIMINATOR — *the server named no message* — not a blanked read.
 * Every caller has a usable value of its own to fall through to; nothing renders
 * this result directly.
 *
 * ⚠️ A `message` array (NestJS's `ValidationPipe` returns `string[]`) answers
 * `null` rather than a joined string: this function's job is the depth, and
 * flattening a field list into a sentence is a rendering decision its callers
 * have not made.
 */
export function serverErrorMessage(error: unknown): string | null {
  const body = responseBodyOf(error)
  if (body === null) return null

  const envelope: unknown = body.error
  if (isRecordLike(envelope)) {
    const nested: unknown = envelope.message
    if (typeof nested === "string" && nested !== "") return nested
  }

  const flat: unknown = body.message
  return typeof flat === "string" && flat !== "" ? flat : null
}

/**
 * The `details` object the SERVER folded onto this error, or `null`.
 *
 * Same wire invariant as {@link serverErrorCode}, one key over.
 * `createApiError(code, message, details)` folds every key the envelope has no
 * slot for into `error.details`, so a refusal's structured payload lands at
 * `data.error.details` and NOWHERE ELSE — never as a sibling of `code`.
 * `TracksExceptionFilter`, which spreads siblings into `error`, is the outlier
 * rather than the pattern, and a fixture copied from it asserts a shape the wire
 * does not send.
 *
 * `null` is a DISCRIMINATOR — *the server folded no details onto this error* —
 * not a blanked read. A caller reading one field out of it still has to decide
 * what a missing field means, and every one of them has a usable value of its
 * own to fall through to.
 *
 * ⚠️ RETURNS THE RAW OBJECT, DELIBERATELY UNVALIDATED. Details are per-code by
 * construction: `FEATURE_ACCESS_RESTRICTED` folds `featureKey`/`featureName`,
 * `CAPABILITY_UNAVAILABLE` folds `capability`. This function owns the DEPTH,
 * which is the part every reader shares; the SHAPE belongs to the code's own
 * contract, and the caller parses it with that contract's schema.
 */
export function serverErrorDetails(error: unknown): Record<string, unknown> | null {
  const body = responseBodyOf(error)
  if (body === null) return null

  const envelope: unknown = body.error
  if (!isRecordLike(envelope)) return null

  const details: unknown = envelope.details
  return isRecordLike(details) ? details : null
}

/**
 * The response body behind a thrown API error, or `null`.
 *
 * Deliberately a structural walk rather than {@link getErrorData}: that helper
 * gates on `instanceof AxiosError`, and the readers above are handed react-query
 * `onError` arguments whose provenance nothing guarantees.
 *
 * 🔴 THE `instanceof Error` REFUSAL IS LOAD-BEARING, NOT DEFENSIVE. Without a
 * `response` this falls through to the already-unwrapped-body branch, and a bare
 * `Error` satisfies `isRecordLike` — so `new Error("socket hang up").message`
 * would be returned as *the server's message*. It is the same trap as axios's own
 * `code`: a reader that always finds a string, about something else entirely.
 */
function responseBodyOf(error: unknown): Record<string, unknown> | null {
  if (!isRecordLike(error)) return null

  const response: unknown = error.response
  if (isRecordLike(response)) {
    return isRecordLike(response.data) ? response.data : null
  }

  return error instanceof Error ? null : error
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}
